'use client';

/**
 * CallStatusCluster — the single status slot under the desktop call orb.
 * Exactly ONE glyph shows at a time (the owner's "duplicate indicators"
 * fix: the orange agent bars used to render statically above the green
 * listening cue):
 *
 *   AGENT     → orange 4-bar multi-tone bars + "Sara is speaking"
 *   USER      → green LiveLevelCue bars + "Sara is listening"
 *   IDLE      → single pulsing dot + "Ready to listen"
 *   THINKING  → three waving dots + "Sara is thinking"
 *
 * All four layers live in one fixed-height stacked grid cell
 * (col-start-1 row-start-1 — the same overlay pattern LiveLevelCue uses
 * internally) and cross-fade on vizMode, so the cluster never shifts
 * layout. The USER layer's fade is owned by LiveLevelCue itself (its
 * delayed fade-out absorbs the engine's activity-timeout blips).
 *
 * Turn 13 (13a "The four states") split what used to be ONE combined
 * "IDLE/THINKING → plain text" layer into two distinct glyphs — each
 * mirrors the EXACT SAME 400ms anti-strobe delay the old combined layer
 * used (fade IN delayed 400ms, fade OUT instant), so a sub-400ms blip
 * between either of them and USER/AGENT still can't strobe. THINKING
 * specifically also covers the THINKING→IDLE→AGENT hop useAudioVisualization
 * can produce around a tool call (resetToIdle() forces an instant IDLE frame
 * before the next tick detects agent audio) — that transient IDLE frame is
 * shorter than the 400ms delay, so it never visibly renders, exactly like
 * any other blip this mechanism already absorbs.
 *
 * Turn 15 (15d) adds a FIFTH glyph, "Resting a moment", shown while a
 * rate-limit retry budget is EXHAUSTED (useCallMode's modelBusy ===
 * 'exhausted') — driven by the `resting` prop, not vizMode, since the
 * backend's turn_state underneath is irrelevant once retries have stopped.
 * Reuses the exact same stacked-grid/cross-fade mechanism as the four
 * mode-driven glyphs above: `resting` simply forces all four `isX` checks
 * false so the normal glyphs can't show alongside it.
 *
 * [FB34/Turn 16] `statusOverride` is a SIXTH glyph, the same shape as
 * `resting`, for the call-start phase machine's pre-live states
 * (`connecting`/`failed` — see src/lib/call-phase.ts): when set, it wins
 * over every other glyph (resting included) and shows its plain string
 * verbatim. CallScreen computes the actual copy per phase/reason and hands
 * it down here as one string, so this component doesn't need to know about
 * call-phase.ts's reason enum at all.
 *
 * Turn 16 adds two presentation toggles CallScreen also computes, both
 * false by default (matching every OTHER call site's plain-text override —
 * e.g. `resting`):
 *   `statusOverrideDots` — the three blink dots next to "Connecting…"
 *   (16a/16d); true only while `callPhase === 'connecting'` (both the plain
 *   dial and the waiting-on-mic sub-state share this SAME under-orb label —
 *   only the notice card differs, see call-start-notice.tsx). False for
 *   every `failed` variant, dots-less per 16c's mocks.
 *   `statusOverrideWarm` — the warm connecting/reconnecting label tone
 *   (T13_STATUS_COLORS.connectingLabel) instead of the resting/failed tone;
 *   true whenever `statusOverrideDots` is (connecting) AND for the calm
 *   `failed(ws_closed)` "Reconnecting…" variant (16c) — both share the same
 *   #9a7c58 tone in the mocks even though only one shows dots.
 */

import { VizMode } from '@/types/chat';
import { cn } from '@/lib/utils';
import {
  EqualizerBars,
  EQUALIZER_AGENT_COLORS,
} from '@/components/assistant/primitives/equalizer-bars';
import { LiveLevelCue } from './live-level-cue';
import { T13_STATUS_COLORS } from '@/lib/design-tokens';
import type { AudioLevels } from '@/hooks/useAudioVisualization';

/** Status line under the orb — Turn 13's copy set (13a): "Ready to listen"
 *  → "Sara is listening" → "Sara is thinking" → "Sara is speaking". USER's
 *  entry is unused here (that mode renders LiveLevelCue, which owns its own
 *  label) but kept so the record stays total over VizMode. */
export const CALL_STATUS_TEXT: Record<VizMode, string> = {
  [VizMode.IDLE]: 'Ready to listen',
  [VizMode.USER]: 'Sara is listening',
  [VizMode.AGENT]: 'Sara is speaking',
  [VizMode.THINKING]: 'Sara is thinking',
};

/** Turn 13's 4-bar AGENT set (13a): 3px-wide bars, specific heights chosen to
 *  read as a lively-but-static baseline before the level engine takes over
 *  (same role the old 5-bar MD heights played). */
const AGENT_BAR_HEIGHTS = [7, 14, 10, 16];

export interface CallStatusClusterProps {
  vizMode: VizMode;
  /** Shared audio level engine ref — feeds both bar glyphs. */
  levelRef: React.RefObject<AudioLevels>;
  className?: string;
  /** Turn 15 (15d) — true while a rate-limit retry budget is EXHAUSTED.
   *  Overrides every vizMode-driven glyph with "Resting a moment". */
  resting?: boolean;
  /** [FB34] Non-empty while the call-phase machine is `connecting`/`failed`
   *  — overrides every other glyph (resting included) with this exact
   *  string. See the file header's FB34 note. */
  statusOverride?: string;
  /** [Turn 16] Three blink dots next to `statusOverride` — see file header. */
  statusOverrideDots?: boolean;
  /** [Turn 16] Warm connecting/reconnecting label tone — see file header. */
  statusOverrideWarm?: boolean;
}

export function CallStatusCluster({
  vizMode,
  levelRef,
  className,
  resting = false,
  statusOverride,
  statusOverrideDots = false,
  statusOverrideWarm = false,
}: CallStatusClusterProps) {
  const hasOverride = !!statusOverride;
  const isAgent = !hasOverride && !resting && vizMode === VizMode.AGENT;
  const isUser = !hasOverride && !resting && vizMode === VizMode.USER;
  const isIdle = !hasOverride && !resting && vizMode === VizMode.IDLE;
  const isThinking = !hasOverride && !resting && vizMode === VizMode.THINKING;
  const isResting = !hasOverride && resting;

  return (
    // Fixed height = the tallest layer (AGENT: 16px bars + 8px gap +
    // ~20px text line) so mode flips never shift the orb above or the
    // controls below.
    <div className={cn('grid h-[46px] place-items-center', className)}>
      {/* AGENT: Sara's voice bars + label — no anti-strobe delay either
          direction (matches the pre-existing convention here). */}
      <div
        className={cn(
          'col-start-1 row-start-1 flex items-center gap-[8px] transition-opacity duration-200',
          isAgent ? 'opacity-[1]' : 'opacity-[0]'
        )}
      >
        <EqualizerBars
          heights={AGENT_BAR_HEIGHTS}
          colors={EQUALIZER_AGENT_COLORS}
          animate={isAgent}
          levelRef={levelRef}
        />
        <span
          className="text-[13.5px] font-semibold"
          style={{ color: T13_STATUS_COLORS.agentLabel }}
        >
          {CALL_STATUS_TEXT[VizMode.AGENT]}
        </span>
      </div>

      {/* USER: green listening cue (self-fades on `active`) */}
      <LiveLevelCue
        className="col-start-1 row-start-1"
        levelRef={levelRef}
        active={isUser}
      />

      {/* IDLE: single pulsing dot + "Ready to listen" (13a). Fade-IN carries
          the same 400ms delay the old combined idle/thinking layer used
          (see file header) — without it, USER→IDLE would superimpose "Sara
          is listening" and this glyph for the delay window, and a
          sub-400ms engine blip would strobe it. */}
      <div
        className={cn(
          'col-start-1 row-start-1 flex items-center gap-[9px] transition-opacity duration-200',
          isIdle ? 'opacity-[1] delay-[400ms]' : 'opacity-[0]'
        )}
      >
        <span
          className="size-[6px] rounded-full animate-wave-dot-idle"
          style={{ backgroundColor: T13_STATUS_COLORS.idleDot }}
        />
        <span
          className="text-[14px] font-medium"
          style={{ color: T13_STATUS_COLORS.idleLabel }}
        >
          {CALL_STATUS_TEXT[VizMode.IDLE]}
        </span>
      </div>

      {/* THINKING: three waving dots + "Sara is thinking" (13a/13c-C). Same
          400ms fade-in delay as IDLE above. */}
      <div
        className={cn(
          'col-start-1 row-start-1 flex items-center gap-[9px] transition-opacity duration-200',
          isThinking ? 'opacity-[1] delay-[400ms]' : 'opacity-[0]'
        )}
      >
        <div className="flex items-center gap-[5px]" aria-hidden="true">
          <span
            className="size-[5px] rounded-full animate-wave-dot-think"
            style={{ backgroundColor: T13_STATUS_COLORS.thinkingDot }}
          />
          <span
            className="size-[5px] rounded-full animate-wave-dot-think"
            style={{
              backgroundColor: T13_STATUS_COLORS.thinkingDot,
              animationDelay: '.25s',
            }}
          />
          <span
            className="size-[5px] rounded-full animate-wave-dot-think"
            style={{
              backgroundColor: T13_STATUS_COLORS.thinkingDot,
              animationDelay: '.5s',
            }}
          />
        </div>
        <span
          className="text-[13.5px] font-semibold"
          style={{ color: T13_STATUS_COLORS.thinkingLabel }}
        >
          {CALL_STATUS_TEXT[VizMode.THINKING]}
        </span>
      </div>

      {/* EXHAUSTED (Turn 15, 15d): "Resting a moment" — no dot/icon, just
          the muted label; the orb itself (dimmed + desaturated) carries the
          visual "not working right now" signal. No anti-strobe delay: this
          is a deliberate, user-driven state (only reachable after the
          backend gives up retrying), not a blippy engine signal. */}
      <div
        className={cn(
          'col-start-1 row-start-1 flex items-center transition-opacity duration-200',
          isResting ? 'opacity-[1]' : 'opacity-[0]'
        )}
      >
        <span
          className="text-[13.5px] font-medium"
          style={{ color: T13_STATUS_COLORS.restingLabel }}
        >
          Resting a moment
        </span>
      </div>

      {/* [FB34/Turn 16] connecting/failed override — no anti-strobe delay (a
          deliberate, driver-decided state, not a blippy engine signal — same
          reasoning as EXHAUSTED above). `statusOverrideDots` adds the
          three-dot "Connecting…" cadence (16a/16d); `statusOverrideWarm`
          swaps in the connecting/reconnecting tone (16a/16c) over the
          resting/failed one. The dots-only case also carries 16e's entrance
          rise (+90ms) — since this class only APPEARS the very first time
          `statusOverrideDots` turns true (a fresh mount, or a retry after
          `failed` cleared it), the one-shot animation naturally only plays
          on that edge, never on every re-render. */}
      <div
        className={cn(
          'col-start-1 row-start-1 flex items-center gap-[9px] transition-opacity duration-200',
          hasOverride ? 'opacity-[1]' : 'opacity-[0]',
          statusOverrideDots &&
            'motion-safe:animate-call-rise motion-safe:[animation-delay:90ms]'
        )}
      >
        {statusOverrideDots && (
          <div className="flex items-center gap-[5px]" aria-hidden="true">
            <span
              className="size-[5px] rounded-full opacity-[.25] motion-safe:animate-call-dot-blink motion-reduce:opacity-100"
              style={{ backgroundColor: T13_STATUS_COLORS.connectingDot }}
            />
            <span
              className="size-[5px] rounded-full opacity-[.25] motion-safe:animate-call-dot-blink motion-reduce:opacity-100"
              style={{
                backgroundColor: T13_STATUS_COLORS.connectingDot,
                animationDelay: '.24s',
              }}
            />
            <span
              className="size-[5px] rounded-full opacity-[.25] motion-safe:animate-call-dot-blink motion-reduce:opacity-100"
              style={{
                backgroundColor: T13_STATUS_COLORS.connectingDot,
                animationDelay: '.48s',
              }}
            />
          </div>
        )}
        <span
          className={cn(
            statusOverrideDots
              ? 'text-[14px] font-semibold'
              : statusOverrideWarm
                ? 'text-[13px] font-medium'
                : 'text-[13.5px] font-medium'
          )}
          style={{
            color:
              statusOverrideDots || statusOverrideWarm
                ? T13_STATUS_COLORS.connectingLabel
                : T13_STATUS_COLORS.idleLabel,
          }}
        >
          {statusOverride}
        </span>
      </div>
    </div>
  );
}
